import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Grid,
  TextField,
  Button,
  CircularProgress,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
  IconButton,
  Chip
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  Delete as DeleteIcon,
  Send as SendIcon
} from '@mui/icons-material';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import appointmentService from '../../services/appointmentService';
import customerService from '../../services/customerService';

const AppointmentDetail = ({ isEditing = false, isNew = false, isRescheduling = false }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  
  // Form state
  const [formData, setFormData] = useState({
    date: new Date(),
    timeSlot: '',
    status: 'scheduled',
    customer: null,
    notes: '',
    location: '',
  });
  
  // UI state
  const [loading, setLoading] = useState(!isNew);
  const [saveLoading, setSaveLoading] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [customersLoading, setCustomersLoading] = useState(true);
  
  // Load appointment data if editing
  useEffect(() => {
    const fetchData = async () => {
      if (isNew) return;
      
      try {
        setLoading(true);
        const appointmentData = await appointmentService.getAppointment(id);
        
        setFormData({
          date: appointmentData.date ? new Date(appointmentData.date) : new Date(),
          timeSlot: appointmentData.timeSlot || '',
          status: appointmentData.status || 'scheduled',
          customer: appointmentData.customer?._id || null,
          notes: appointmentData.notes || '',
          location: appointmentData.location || '',
        });
      } catch (error) {
        console.error('Error loading appointment:', error);
        setSaveError('Failed to load appointment data. Please try again.');
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, [id, isNew]);
  
  // Load customer list
  useEffect(() => {
    const fetchCustomers = async () => {
      try {
        setCustomersLoading(true);
        const data = await customerService.getCustomers();
        setCustomers(data);
      } catch (error) {
        console.error('Error loading customers:', error);
      } finally {
        setCustomersLoading(false);
      }
    };
    
    fetchCustomers();
  }, []);
  
  // Handle form field changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };
  
  // Handle date change
  const handleDateChange = (newDate) => {
    setFormData(prev => ({ ...prev, date: newDate }));
  };
  
  // Form validation
  const validateForm = () => {
    const errors = [];
    
    if (!formData.date) {
      errors.push('Date is required');
    }
    
    if (!formData.timeSlot) {
      errors.push('Time slot is required');
    }
    
    if (!formData.customer) {
      errors.push('Customer is required');
    }
    
    return errors;
  };
  
  // Handle save appointment
  const handleSave = async () => {
    try {
      setSaveLoading(true);
      setSaveError(null);
      
      // Validate form
      const validationErrors = validateForm();
      if (validationErrors.length > 0) {
        setSaveError(validationErrors.join('. '));
        setSaveLoading(false);
        return;
      }
      
      // Format data for API, mapping frontend state to backend schema
      const formattedData = {
        customerId: formData.customer, // Map 'customer' (ID) to 'customerId'
        date: formData.date.toISOString(), // Convert date to ISO string
        timeSlot: formData.timeSlot, // Keep timeSlot as is
        notes: formData.notes || '', // Keep notes, ensure it's string or empty
        address: formData.location || '', // Map 'location' to 'address'
        // status is not part of the creation schema, only for updates
      };

      if (isNew) {
        // Create new appointment
        await appointmentService.createAppointment(formattedData);
        navigate('/appointments');
      } else {
        // Update the appointment details first.
        await appointmentService.updateAppointment(id, formattedData);

        // The appointment PUT route validates against a Joi schema that has no
        // `status` field (and strips unknown keys), so status can't ride along
        // in the same request — it must go through the dedicated status
        // endpoint. Do it as a second step when rescheduling. (Backend TODO:
        // allow `status` in the update schema so this can be one atomic call.)
        if (isRescheduling) {
          await appointmentService.updateAppointment(id, { status: 'rescheduled' });
        }
        navigate('/appointments');
      }
    } catch (error) {
      console.error('Error saving appointment:', error);
      setSaveError(error.message || 'Failed to save appointment. Please try again.');
    } finally {
      setSaveLoading(false);
    }
  };
  
  // Handle delete appointment
  const handleDelete = async () => {
    if (window.confirm('Are you sure you want to delete this appointment?')) {
      try {
        setSaveLoading(true);
        await appointmentService.deleteAppointment(id);
        navigate('/appointments');
      } catch (error) {
        console.error('Error deleting appointment:', error);
        setSaveError(error.message || 'Failed to delete appointment. Please try again.');
        setSaveLoading(false);
      }
    }
  };
  
  // Handle send to CRM
  const [successMessage, setSuccessMessage] = useState(null);

  const handleSendToCrm = async () => {
    try {
      setSaveLoading(true);
      setSaveError(null);
      setSuccessMessage(null);
      await appointmentService.sendToCrm(id);
      setSuccessMessage('Appointment sent to CRM successfully.');
      setSaveLoading(false);
    } catch (error) {
      console.error('Error sending to CRM:', error);
      setSaveError(
        error.response?.data?.error || error.message || 'Failed to send to CRM. Please try again.'
      );
      setSaveLoading(false);
    }
  };
  
  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="80vh">
        <CircularProgress />
      </Box>
    );
  }
  
  return (
    <Box>
      <Paper sx={{ p: 3, mb: 4 }}>
        <Box display="flex" alignItems="center" mb={3}>
          <IconButton onClick={() => navigate('/appointments')} sx={{ mr: 2 }}>
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h5" component="h1">
            {isNew ? 'New Appointment' : isRescheduling ? 'Reschedule Appointment' : isEditing ? 'Edit Appointment' : 'Appointment Details'}
          </Typography>
          {!isNew && !isEditing && (
            <Chip 
              label={formData.status || 'scheduled'} 
              color={
                formData.status === 'completed' ? 'success' :
                formData.status === 'canceled' ? 'error' :
                formData.status === 'rescheduled' ? 'warning' : 'primary'
              }
              sx={{ ml: 2 }}
            />
          )}
        </Box>
        
        {saveError && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setSaveError(null)}>
            {saveError}
          </Alert>
        )}

        {successMessage && (
          <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccessMessage(null)}>
            {successMessage}
          </Alert>
        )}
        
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DatePicker
                label="Date"
                value={formData.date}
                onChange={handleDateChange}
                disabled={!isEditing && !isNew && !isRescheduling}
                sx={{ width: '100%', mb: 2 }}
                slotProps={{
                  textField: {
                    variant: 'outlined',
                    required: true
                  }
                }}
              />
            </LocalizationProvider>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <TextField
              label="Time Slot"
              name="timeSlot"
              value={formData.timeSlot}
              onChange={handleChange}
              disabled={!isEditing && !isNew && !isRescheduling}
              fullWidth
              required
              placeholder="e.g. 2:00 PM - 3:00 PM"
            />
          </Grid>
          
          <Grid item xs={12}>
            <FormControl fullWidth required>
              <InputLabel>Customer</InputLabel>
              <Select
                name="customer"
                value={formData.customer || ''}
                onChange={handleChange}
                disabled={!isEditing && !isNew}
                label="Customer"
              >
                {customersLoading ? (
                  <MenuItem disabled>Loading customers...</MenuItem>
                ) : (
                  customers.map(customer => (
                    <MenuItem key={customer._id} value={customer._id}>
                      {customer.name}
                    </MenuItem>
                  ))
                )}
              </Select>
            </FormControl>
          </Grid>
          
          {(isEditing || !isNew) && !isRescheduling && (
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  name="status"
                  value={formData.status || 'scheduled'}
                  onChange={handleChange}
                  disabled={!isEditing && !isNew}
                  label="Status"
                >
                  <MenuItem value="scheduled">Scheduled</MenuItem>
                  <MenuItem value="completed">Completed</MenuItem>
                  <MenuItem value="canceled">Canceled</MenuItem>
                  <MenuItem value="rescheduled">Rescheduled</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          )}
          
          <Grid item xs={12} md={isRescheduling || (!isEditing && isNew) ? 12 : 6}>
            <TextField
              label="Location"
              name="location"
              value={formData.location}
              onChange={handleChange}
              disabled={!isEditing && !isNew && !isRescheduling}
              fullWidth
              placeholder="Enter appointment location"
            />
          </Grid>
          
          <Grid item xs={12}>
            <TextField
              label="Notes"
              name="notes"
              value={formData.notes}
              onChange={handleChange}
              disabled={!isEditing && !isNew && !isRescheduling}
              fullWidth
              multiline
              rows={4}
              placeholder="Add notes about this appointment"
            />
          </Grid>
        </Grid>
        
        <Divider sx={{ my: 3 }} />
        
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 1.25,
            justifyContent: 'space-between',
          }}
        >
          <Button
            variant="outlined"
            color="inherit"
            onClick={() => navigate('/appointments')}
            startIcon={<CancelIcon />}
            sx={{ borderColor: 'divider', flex: { xs: '1 1 100%', sm: '0 0 auto' } }}
          >
            Cancel
          </Button>

          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 1.25,
              flex: { xs: '1 1 100%', sm: '0 0 auto' },
              '& > *': { flex: { xs: 1, sm: '0 0 auto' } },
            }}
          >
            {!isNew && !isEditing && (
              <>
                <Button
                  variant="outlined"
                  color="inherit"
                  onClick={() => navigate(`/appointments/${id}/edit`)}
                  sx={{ borderColor: 'divider' }}
                >
                  Edit
                </Button>
                <Button
                  variant="outlined"
                  color="error"
                  onClick={handleDelete}
                  startIcon={<DeleteIcon />}
                >
                  Delete
                </Button>
                <Button
                  variant="contained"
                  onClick={handleSendToCrm}
                  startIcon={<SendIcon />}
                >
                  Send to CRM
                </Button>
              </>
            )}

            {(isEditing || isNew || isRescheduling) && (
              <Button
                variant="contained"
                color="primary"
                onClick={handleSave}
                disabled={saveLoading}
                startIcon={saveLoading ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
              >
                {saveLoading ? 'Saving...' : 'Save'}
              </Button>
            )}
          </Box>
        </Box>
      </Paper>
    </Box>
  );
};

export default AppointmentDetail;